import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/constant.js";
import Pagination from "../Pagination/Pagination";
import { toastr } from 'react-redux-toastr';
import "../../assets/css/recommendation-modal.css";

const Issues = () => {
  const { orgId } = useParams();
  const id = useSelector((state) => {
    return orgId ? orgId : state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
  });

  // All Results state
  const [allResults, setAllResults] = useState([]);
  const [loadingAllResults, setLoadingAllResults] = useState(false);
  const [totalCount, setTotalCount] = useState(0); // Total count from API for pagination

  // VIP Members state (for filter dropdown)
  const [vipMembers, setVipMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState(""); // API-based: leaks, social, crawling
  const [sortOrder, setSortOrder] = useState("desc"); // API-based: desc (default), asc
  const [vipMemberIdFilter, setVipMemberIdFilter] = useState(""); // API-based: VIP member ID
  const [categoryFilter, setCategoryFilter] = useState(""); // API-based: false-positive, remediated
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [openDropdowns, setOpenDropdowns] = useState({}); // Track which dropdowns are open
  const [selectedIssues, setSelectedIssues] = useState(new Set()); // Track selected issues for bulk actions
  const [exportingExcel, setExportingExcel] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [isRecommendationModal, setIsRecommendationModal] = useState(false);
  const [activeRecommendationTab, setActiveRecommendationTab] = useState('guide');

  const vendorName = sessionStorage.getItem("vendorName");
  const vendorId = sessionStorage.getItem("vendorId");

  // Fetch VIP Members (for filter dropdown)
  const fetchVipMembers = async () => {
    if (!id) return;
    setLoadingMembers(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const response = await axios.get(`${API_URL}/executive-monitoring/${id}/vip-members`);
      setVipMembers(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching VIP members:", error);
      setVipMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const buildFilterQueryString = (options = { includePagination: true }) => {
    const queryParams = new URLSearchParams();
    if (moduleFilter) {
      queryParams.append("module", moduleFilter);
    }
    if (sortOrder) {
      queryParams.append("sort_order", sortOrder);
    }
    if (vipMemberIdFilter) {
      queryParams.append("vipMemberId", vipMemberIdFilter);
    }
    if (severityFilter) {
      queryParams.append("severity", severityFilter);
    }
    if (categoryFilter) {
      queryParams.append("category", categoryFilter);
    }
    if (options.includePagination) {
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", pageSize.toString());
    }
    return queryParams.toString();
  };

  // Fetch All Executive Results
  const fetchAllResults = async () => {
    if (!id) return;
    setLoadingAllResults(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const queryString = buildFilterQueryString({ includePagination: true });
      const url = queryString
        ? `${API_URL}/executive-monitoring/${id}/results?${queryString}`
        : `${API_URL}/executive-monitoring/${id}/results`;

      const response = await axios.get(url);
      const responseData = response.data?.data;
      
      // Extract pagination metadata from API response
      // API returns pagination object with totalResults, totalPages, etc.
      // Check multiple possible locations for pagination info
      const paginationInfo = response.data?.pagination || responseData?.pagination || response.data?.data?.pagination || {};
      const total = paginationInfo.totalResults || paginationInfo.total || paginationInfo.totalCount || paginationInfo.totalItems || 0;
      
      setTotalCount(total);
      
      // Create maps for quick lookup
      const vipMemberMap = new Map();
      const scanJobMap = new Map();
      
      // Build VIP member map from scanJobs
      if (responseData?.scanJobs && Array.isArray(responseData.scanJobs)) {
        responseData.scanJobs.forEach((scanJob) => {
          const scanJobId = scanJob._id || scanJob.id;
          if (scanJobId) {
            scanJobMap.set(scanJobId, scanJob);
          }
          
          // Map VIP member by ID
          if (scanJob.vipMemberId) {
            const vipId = typeof scanJob.vipMemberId === "object" 
              ? scanJob.vipMemberId._id 
              : scanJob.vipMemberId;
            if (vipId && !vipMemberMap.has(vipId)) {
              const vipMember = typeof scanJob.vipMemberId === "object"
                ? scanJob.vipMemberId
                : null;
              if (vipMember) {
                vipMemberMap.set(vipId, vipMember);
              }
            }
          }
        });
      }
      
      // Process results array (API returns paginated results)
      const flattenedResults = [];
      if (responseData?.results && Array.isArray(responseData.results)) {
        responseData.results.forEach((result) => {
          const scanJobId = result.scanJobId;
          const vipMemberId = result.vipMemberId;
          
          // Get VIP member info
          const vipId = typeof vipMemberId === "object" ? vipMemberId._id : vipMemberId;
          const vipMember = vipId ? vipMemberMap.get(vipId) : null;
          
          // Get scan job info
          const scanJob = scanJobId ? scanJobMap.get(scanJobId) : null;
          
          // Determine module type based on result type
          let module = "unknown";
          let moduleType = "Unknown";
          
          if (result.type === "social_profile") {
            module = "social";
            moduleType = "Social Media";
          } else if (result.type === "email_registration" || result.type === "pii" || result.type === "leaked_credential") {
            module = "leaks";
            moduleType = "Leaks";
          } else if (result.type === "crawling" || result.type === "crawled_content" || result.type?.toLowerCase().includes("crawl")) {
            module = "crawling";
            moduleType = "Crawling";
          }
          
          // Extract description based on result type
          let description = result.description || result.message || result.title || result.content;
          
          if (result.type === "social_profile" && result.profileData) {
            // For social media, prioritize summary or risk_analysis from profileData
            description = result.profileData.summary || result.profileData.risk_analysis || description;
            // If still no description, try to create a meaningful one
            if (!description || description === result.description) {
              const profileInfo = [];
              if (result.profileData.name) profileInfo.push(`Profile: ${result.profileData.name}`);
              if (result.profileData.username) profileInfo.push(`Username: ${result.profileData.username}`);
              if (result.profileData.platform) profileInfo.push(`Platform: ${result.profileData.platform}`);
              description = profileInfo.length > 0 ? profileInfo.join(" | ") : description;
            }
          } else if (result.type === "email_registration") {
            description = `Email registered on ${result.registration || "unknown platform"}`;
          } else if (result.type === "pii") {
            description = `PII found in ${result.dbname || "unknown database"}`;
            if (result.name) description += ` - Name: ${result.name}`;
            if (result.address) description += `, Address: ${result.address}`;
          } else if (result.type === "leaked_credential") {
            description = `Leaked credentials found in ${result.dbname || "unknown database"}`;
          } else if (result.type === "crawled_content") {
            // For crawled content, create description from URL
            if (result.url) {
              try {
                const urlObj = new URL(result.url);
                description = `Crawled content found at ${urlObj.hostname}${urlObj.pathname}`;
              } catch (e) {
                description = `Crawled content found at ${result.url}`;
              }
            } else {
              description = description || "Crawled content detected";
            }
          }
          
          // Ensure description is a string, not an object
          if (description && typeof description !== "string") {
            description = String(description);
          }
          
          // Extract source/platform
          let source = result.source || result.platform || result.origin || result.domain;
          if (!source && result.type === "email_registration") {
            source = result.registration;
          } else if (!source && result.type === "pii") {
            source = result.dbname;
          } else if (!source && result.type === "leaked_credential") {
            source = result.dbname;
          } else if (!source && result.type === "social_profile") {
            source = result.profileData?.platform || "Social Media";
          } else if (!source && result.type === "crawled_content") {
            // Extract domain from URL for crawled content
            if (result.url) {
              try {
                const urlObj = new URL(result.url);
                source = urlObj.hostname;
              } catch (e) {
                source = result.url.split('/')[2] || result.url || "Unknown";
              }
            } else {
              source = "Crawled Content";
            }
          }
          
          // Extract URL
          let url = result.url || result.link || result.profileUrl;
          if (!url && result.type === "social_profile" && result.profileData) {
            url = result.profileData.url;
          }
          
          // Extract screenshot for social media
          let screenshot = result.screenshot || result.screenshot_url;
          if (!screenshot && result.type === "social_profile" && result.profileData) {
            screenshot = result.profileData.screenshot || result.profileData.screenshot_url;
          }
          
          // Extract detected date
          const detectedAt = result.scannedAt || result.createdAt || result.detectedAt || result.timestamp || result.date;
          
          // Final fallback for description - avoid JSON.stringify if possible
          let finalDescription = description;
          if (!finalDescription || finalDescription.trim() === "") {
            // Only use JSON.stringify as last resort, and only for non-social profiles
            if (result.type !== "social_profile") {
              finalDescription = JSON.stringify(result).substring(0, 100);
            } else {
              // For social profiles, create a basic description
              finalDescription = `Social media profile detected${result.profileData?.platform ? ` on ${result.profileData.platform}` : ""}`;
            }
          }
          
          flattenedResults.push({
            ...result,
            scanId: scanJobId,
            scanJob,
            module,
            moduleType,
            description: finalDescription,
            source: source || "N/A",
            url,
            detectedAt,
            screenshot, // Include screenshot in flattened result
          });
        });
      }
      
      setAllResults(flattenedResults);
      // If API doesn't return pagination info, use results length as fallback
      if (!total && flattenedResults.length > 0) {
        setTotalCount(flattenedResults.length);
      }
    } catch (error) {
      console.error("Error fetching all results:", error);
      setAllResults([]);
      setTotalCount(0);
    } finally {
      setLoadingAllResults(false);
    }
  };

  const downloadExcelReport = async () => {
    if (!id) return;
    setExportingExcel(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const queryString = buildFilterQueryString({ includePagination: false });
      const url = queryString
        ? `${API_URL}/executive-monitoring/${id}/excel?${queryString}`
        : `${API_URL}/executive-monitoring/${id}/excel`;

      const response = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], {
        type: response.data?.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "executive-monitoring-report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading Excel report:", error);
      toastr.error('Error', error.response?.data?.userMessage || "Failed to download Excel report");
    } finally {
      setExportingExcel(false);
    }
  };

  const openPdfReport = async () => {
    if (!id) return;
    setLoadingPdf(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const queryString = buildFilterQueryString({ includePagination: false });
      const url = queryString
        ? `${API_URL}/executive-monitoring/${id}/pdf?${queryString}`
        : `${API_URL}/executive-monitoring/${id}/pdf`;

      // API returns HTML, so we use text response type
      const response = await axios.get(url, { responseType: "text" });
      const htmlContent = response.data;

      // Open a new window and write the HTML content
      const pdfWindow = window.open("", "_blank");

      if (pdfWindow) {
        console.log(htmlContent, "htmlContent");
        pdfWindow.document.write(`
                ${htmlContent}
        `);
        pdfWindow.document.close();
        
        // Focus the window
        pdfWindow.focus();
      } else {
        toastr.error('Error', 'Please allow pop-ups to view the report');
      }
    } catch (error) {
      console.error("Error fetching PDF report:", error);
      toastr.error('Error', error.response?.data?.userMessage || "Failed to open PDF report");
    } finally {
      setLoadingPdf(false);
    }
  };

  // Validate and sanitize email to prevent XSS
  const validateEmail = (email) => {
    if (!email || typeof email !== "string") return null;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return null;
    
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) return null;
    
    // Reject dangerous patterns
    if (/<script|javascript:|on\w+\s*=/i.test(trimmedEmail)) {
      return null;
    }
    
    // Encode email to prevent XSS
    return encodeURIComponent(trimmedEmail);
  };

  // Validate and sanitize URL to prevent XSS
  const validateUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    
    // Remove any whitespace and check if empty
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return null;
    
    // Reject dangerous protocols and XSS patterns
    const dangerousPatterns = /^(javascript|data|vbscript|file|about|on\w+):/i;
    if (dangerousPatterns.test(trimmedUrl)) {
      return null;
    }
    
    // Reject URLs containing script tags or event handlers
    if (/<script|on\w+\s*=/i.test(trimmedUrl)) {
      return null;
    }
    
    try {
      let urlToValidate = trimmedUrl;
      
      // Ensure URL starts with http:// or https://
      if (!trimmedUrl.match(/^https?:\/\//i)) {
        urlToValidate = `https://${trimmedUrl}`;
      }
      
      // Use URL constructor to validate and normalize the URL
      // This will throw an error for invalid URLs
      const urlObj = new URL(urlToValidate);
      
      // Only allow http and https protocols (explicit check)
      const allowedProtocols = ["http:", "https:"];
      if (!allowedProtocols.includes(urlObj.protocol)) {
        return null;
      }
      
      // Additional validation: ensure hostname exists and is valid
      if (!urlObj.hostname || urlObj.hostname.length === 0) {
        return null;
      }
      
      // Return the validated and sanitized URL
      // The URL constructor normalizes and sanitizes the URL automatically
      // urlObj.href returns a properly formatted, safe URL string
      return urlObj.href;
    } catch (e) {
      // Invalid URL format - reject it
      return null;
    }
  };

  useEffect(() => {
    if (id) {
      fetchVipMembers();
      fetchAllResults();
    }
  }, [id, moduleFilter, sortOrder, vipMemberIdFilter, severityFilter, categoryFilter, currentPage, pageSize]);

  // Filter results (only client-side search, all other filters are API-based)
  const filteredResults = useMemo(() => {
    let filtered = allResults;

    // Apply search filter (client-side only)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((result) => {
        const vipMember = result?.vipMember || {};
        const vipMemberName = typeof vipMember === "object" 
          ? vipMember.name || (Array.isArray(vipMember.email) ? vipMember.email[0] : vipMember.email) || ""
          : vipMember || "";
        const description = result.description || result.message || result.title || result.content || "";
        const source = result.source || result.platform || result.origin || result.domain || "";
        const type = result.type || result.category || result.moduleType || result.module || "";
        const scanId = result.scanId || "";

        return (
          vipMemberName.toLowerCase().includes(searchLower) ||
          description.toLowerCase().includes(searchLower) ||
          source.toLowerCase().includes(searchLower) ||
          type.toLowerCase().includes(searchLower) ||
          scanId.toLowerCase().includes(searchLower)
        );
      });
    }

    // Note: Module, severity, sort_order, and vipMemberId filters are now API-based
    // Note: Only search filter remains client-side

    return filtered;
  }, [allResults, searchTerm]);

  // Note: Pagination is now API-based, so we use allResults directly
  // Client-side filtering still applies (search and severity)
  const paginatedResults = filteredResults; // API already returns paginated results, we just apply client-side filters

  // Reset to page 1 when API-based filters change (module, sort, vipMember, severity, category)
  useEffect(() => {
    setCurrentPage(1);
    setExpandedDescriptions(new Set());
  }, [moduleFilter, sortOrder, vipMemberIdFilter, severityFilter, categoryFilter]);
  
  // Reset to page 1 when client-side filters change (search only)
  useEffect(() => {
    setCurrentPage(1);
    setExpandedDescriptions(new Set());
  }, [searchTerm]);
  
  // Reset expanded descriptions when page changes
  useEffect(() => {
    setExpandedDescriptions(new Set());
    setOpenDropdowns({}); // Close all dropdowns when page changes
  }, [currentPage]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside a dropdown menu or button
      const clickedElement = event.target;
      const isInsideDropdown = clickedElement.closest('.dropdown-menu') || 
                               clickedElement.closest('.dropdown-toggle') ||
                               clickedElement.closest('.btn-group');
      
      if (Object.keys(openDropdowns).length > 0 && !isInsideDropdown) {
        setOpenDropdowns({});
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);
  
  // Handle show details
  const handleShowDetails = (result) => {
    setSelectedIssue(result);
    setShowDetailsModal(true);
  };
  
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedIssue(null);
  };
  
  // Handle expand/collapse description
  const toggleDescription = (index) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSnoozeAction = async (issueId, action) => {
    
    if (!id || !issueId) {
      console.error("Missing id or issueId:", { id, issueId });
      toastr.error('Error', 'Missing issue ID');
      return;
    }
    
    // Close dropdown first
    setOpenDropdowns({});
    
    try {
      const token = localStorage.getItem("session");
      if (!token) {
        toastr.error('Error', 'Authentication token not found');
        return;
      }
      
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const url = `${API_URL}/executive-monitoring/${id}/results/unSnooze`;
      
      // Handle both single ID and array of IDs
      const issueIdsArray = Array.isArray(issueId) ? issueId : [issueId];
      
      const payload = { 
        issueIds: issueIdsArray,
        action: action
      };
      
      
      const response = await axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      
      // Check for success in response - handle different response formats
      const isSuccess = response.data && (
        response.data.status === 200 || 
        response.data.success === true || 
        response.data.error === false ||
        (response.status === 200 && !response.data.error)
      );
      
      if (isSuccess) {
        // Clear selected issues if it was a bulk action
        if (Array.isArray(issueId) && issueId.length > 1) {
          setSelectedIssues(new Set());
        }
        // Refresh the results after action
        await fetchAllResults();
        // Show success message
        const issueCount = Array.isArray(issueId) ? issueId.length : 1;
        const actionName = action === 'false-positive' ? 'Unsnoozed from False Positive' : 
                          action === 'remidiated' ? 'Unsnoozed from Remediated' : 
                          action === 'markasLegitimate' ? 'Unsnoozed' : action;
        const successMessage = response.data?.userMessage || response.data?.message || `${issueCount} issue(s) ${actionName} successfully`;
        toastr.success('Success', successMessage);
      } else {
        // Handle case where response exists but indicates failure
        const errorMessage = response.data?.userMessage || response.data?.message || `Failed to unsnooze issue(s)`;
        toastr.error('Error', errorMessage);
      }
    } catch (error) {
      console.error("Error performing unsnooze action:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/executive-monitoring/${id}/results/unSnooze`
      });
      
      // More detailed error message
      let errorMessage = `Failed to unsnooze issue(s)`;
      if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastr.error('Error', errorMessage);
    }
  };
  // Handle issue action (mark as false positive or remediated)
  const handleIssueAction = async (issueId, action) => {
    
    if (!id || !issueId) {
      console.error("Missing id or issueId:", { id, issueId });
      toastr.error('Error', 'Missing issue ID');
      return;
    }
    
    // Close dropdown first
    setOpenDropdowns({});
    
    try {
      const token = localStorage.getItem("session");
      if (!token) {
        toastr.error('Error', 'Authentication token not found');
        return;
      }
      
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const url = `${API_URL}/executive-monitoring/${id}/results/action`;
      const payload = { 
        issueIds: [issueId],
        action: action
      };
      
      
      const response = await axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      
      // Check for success in response - handle different response formats
      const isSuccess = response.data && (
        response.data.status === 200 || 
        response.data.success === true || 
        response.data.error === false ||
        (response.status === 200 && !response.data.error)
      );
      
      if (isSuccess) {
        // Refresh the results after action
        await fetchAllResults();
        // Show success message
        const actionName = action === 'markAsFalsePositive' ? 'False Positive' : 
                          action === 'markAsRemidiated' ? 'Remediated' : 
                          action === 'markasLegitimate' ? 'Legitimate' : action;
        const successMessage = response.data?.userMessage || response.data?.message || `Issue marked as ${actionName} successfully`;
        toastr.success('Success', successMessage);
      } else {
        // Handle case where response exists but indicates failure
        const errorMessage = response.data?.userMessage || response.data?.message || `Failed to mark issue as ${action}`;
        toastr.error('Error', errorMessage);
      }
    } catch (error) {
      console.error("Error performing issue action:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/executive-monitoring/${id}/results/action`
      });
      
      // More detailed error message
      let errorMessage = `Failed to mark issue as ${action === 'markAsFalsePositive' ? 'False Positive' : 
                        action === 'markAsRemidiated' ? 'Remediated' : 
                        action === 'markasLegitimate' ? 'Legitimate' : action}`;
      if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastr.error('Error', errorMessage);
    }
  };

  // Handle multi-issue action (bulk actions)
  const handleMultiIssueAction = async (issueIds, action) => {
    console.log("handleMultiIssueAction called with:", { issueIds, action, organisationId: id });
    
    if (!id || !issueIds || issueIds.length === 0) {
      console.error("Missing id or issueIds:", { id, issueIds });
      toastr.error('Error', 'No issues selected');
      return;
    }
    
    // Close dropdown first
    setOpenDropdowns({});
    
    try {
      const token = localStorage.getItem("session");
      if (!token) {
        toastr.error('Error', 'Authentication token not found');
        return;
      }
      
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const url = `${API_URL}/executive-monitoring/${id}/results/action`;
      const payload = { 
        issueIds: issueIds,
        action: action
      };
      
      console.log("Calling multi-action API:", { url, payload, issueIds, organisationId: id });
      
      const response = await axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log("Multi-action API response:", response.data);
      
      // Check for success in response - handle different response formats
      const isSuccess = response.data && (
        response.data.status === 200 || 
        response.data.success === true || 
        response.data.error === false ||
        (response.status === 200 && !response.data.error)
      );
      
      if (isSuccess) {
        // Clear selected issues
        setSelectedIssues(new Set());
        // Refresh the results after action
        await fetchAllResults();
        // Show success message
        const actionName = action === 'markAsFalsePositive' ? 'False Positive' : 
                          action === 'markAsRemidiated' ? 'Remediated' : 
                          action === 'markasLegitimate' ? 'Legitimate' : action;
        const successMessage = response.data?.userMessage || response.data?.message || `${issueIds.length} issue(s) marked as ${actionName} successfully`;
        toastr.success('Success', successMessage);
      } else {
        // Handle case where response exists but indicates failure
        const errorMessage = response.data?.userMessage || response.data?.message || `Failed to mark issues as ${action}`;
        toastr.error('Error', errorMessage);
      }
    } catch (error) {
      console.error("Error performing multi-issue action:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${API_URL}/executive-monitoring/${id}/results/action`
      });
      
      // More detailed error message
      let errorMessage = `Failed to mark issues as ${action}`;
      if (error.response?.data?.userMessage) {
        errorMessage = error.response.data.userMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toastr.error('Error', errorMessage);
    }
  };

  // Toggle issue selection
  const toggleIssueSelection = (issueId) => {
    setSelectedIssues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(issueId)) {
        newSet.delete(issueId);
      } else {
        newSet.add(issueId);
      }
      return newSet;
    });
  };

  // Toggle select all issues
  const toggleSelectAll = () => {
    if (selectedIssues.size === paginatedResults.length) {
      setSelectedIssues(new Set());
    } else {
      const allIssueIds = paginatedResults
        .map(result => result?.issueId || result?._id || result?.id)
        .filter(id => id);
      setSelectedIssues(new Set(allIssueIds));
    }
  };

  // Note: uniqueSeverities removed - using static options (low, medium, high) since pagination means we don't see all results
  // Note: uniqueModules removed since module filter is now API-based with fixed options (leaks, social, crawling)

  // Truncate text with tooltip
  const truncateText = (text, maxLength = 100) => {
    if (!text || typeof text !== "string") return "N/A";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div className="container-fluid">
      <div className="block-header">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1 className="welcome-text">Executive Monitoring Issues</h1>
            <nav aria-label="breadcrumb" className="mt-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  {vendorId ? (
                    <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                      Forest View
                    </Link>
                  ) : (
                    <Link to="/" className="welcome-textSmall">
                      Home
                    </Link>
                  )}
                </li>
                {vendorName && (
                  <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                    {vendorName}
                  </li>
                )}
                <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                  Issues
                </li>
              </ol>
              <br />
            </nav>
          </div>
        </div>
      </div>

      {/* Issues List */}
      <div className="card">
        <div className="body">
          {/* Header with filters */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <div className="d-flex align-items-center gap-3 flex-wrap mb-2 mb-md-0">
              <h6 className="welcome-textSmall mb-0" style={{ fontSize: "14px" }}>
                Issues ({filteredResults.length} of {totalCount})
              </h6>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setIsRecommendationModal(true)}
                style={{ fontSize: "12px" }}
                title="View Recommendations"
              >
                <i className="fa fa-lightbulb-o mr-1"></i>
                Recommendation
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={downloadExcelReport}
                disabled={exportingExcel}
                style={{ fontSize: "12px" }}
              >
                <i className={`fa ${exportingExcel ? "fa-spinner fa-spin" : "fa-file-excel-o"} mr-1`}></i>
                {exportingExcel ? "Exporting..." : "Export Excel"}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={openPdfReport}
                disabled={loadingPdf}
                style={{ fontSize: "12px" }}
              >
                <i className={`fa ${loadingPdf ? "fa-spinner fa-spin" : "fa-file-pdf-o"} mr-1`}></i>
                {loadingPdf ? "Opening..." : "View PDF"}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchAllResults}
                disabled={loadingAllResults}
                style={{ fontSize: "12px" }}
              >
                <i className={`fa ${loadingAllResults ? "fa-spinner fa-spin" : "fa-refresh"} mr-1`}></i>
                {loadingAllResults ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          {/* Category Filter Tabs */}
          <div className="mb-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <ul className="nav nav-tabs category-filter-tabs" role="tablist" style={{ maxWidth: "450px" }}>
                <li className="nav-item">
                  <a
                    className={`nav-link ${categoryFilter === "" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setCategoryFilter("");
                      setCurrentPage(1);
                    }}
                    style={{ cursor: "pointer", textAlign: "center", fontSize: "13px" }}
                  >
                    All
                  </a>
                </li>
                <li className="nav-item" style={{ flex: "1.3" }}>
                  <a
                    className={`nav-link ${categoryFilter === "false-positive" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setCategoryFilter("false-positive");
                      setCurrentPage(1);
                    }}
                    style={{ cursor: "pointer", textAlign: "center", fontSize: "13px" }}
                  >
                    False Positive
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${categoryFilter === "remidiated" ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setCategoryFilter("remidiated");
                      setCurrentPage(1);
                    }}
                    style={{ cursor: "pointer", textAlign: "center", fontSize: "13px" }}
                  >
                    Remediated
                  </a>
                </li>
              </ul>
              {/* Unsnooze Button - Show when false-positive or remediated tab is active */}
              {(categoryFilter === "false-positive" || categoryFilter === "remidiated") && paginatedResults.length > 0 && (
                <button
                  className="btn btn-sm btn-info"
                  onClick={async () => {
                    // Get all issue IDs from current page results
                    const allIssueIds = paginatedResults
                      .map(result => result?.issueId || result?._id || result?.id)
                      .filter(id => id);
                    
                    if (allIssueIds.length > 0) {
                      await handleMultiIssueAction(allIssueIds, 'markasLegitimate');
                    } else {
                      toastr.error('Error', 'No issues found to unsnooze');
                    }
                  }}
                  style={{ fontSize: "13px" }}
                >
                  <i className="fa fa-undo mr-1"></i> Unsnooze All
                </button>
              )}
            </div>
          </div>

          {/* Search and Filter Controls */}
          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-2">
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text">
                    <i className="fa fa-search"></i>
                  </span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search issues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <div className="input-group-append">
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setSearchTerm("")}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="col-12 col-md-3 mb-2">
              <select
                className="form-control"
                value={severityFilter}
                onChange={(e) => {
                  setSeverityFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Severities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-control"
                value={moduleFilter}
                onChange={(e) => {
                  setModuleFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Modules</option>
                <option value="leaks">Leaks</option>
                <option value="social">Social</option>
                <option value="crawling">Crawling</option>
              </select>
            </div>
            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-control"
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-control"
                value={vipMemberIdFilter}
                onChange={(e) => {
                  setVipMemberIdFilter(e.target.value);
                  setCurrentPage(1);
                }}
                disabled={loadingMembers}
              >
                <option value="">All VIP Members</option>
                {vipMembers.map((member) => {
                  const memberId = member._id || member.id;
                  const memberName = member.name || (Array.isArray(member.email) ? member.email[0] : member.email) || "Unknown";
                  return (
                    <option key={memberId} value={memberId}>
                      {memberName}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-12 col-md-2 mb-2">
              {(searchTerm || severityFilter || moduleFilter || vipMemberIdFilter || categoryFilter) && (
                <button
                  className="btn btn-outline-secondary btn-block"
                  onClick={() => {
                    setSearchTerm("");
                    setSeverityFilter("");
                    setModuleFilter("");
                    setVipMemberIdFilter("");
                    setCategoryFilter("");
                    setSortOrder("desc");
                    setCurrentPage(1);
                  }}
                >
                  <i className="fa fa-times mr-1"></i> Clear
                </button>
              )}
            </div>
          </div>

          {loadingAllResults ? (
            <div className="text-center p-5">
              <i className="fa fa-spinner fa-spin fa-2x mb-3" style={{ color: "#667eea" }}></i>
              <p className="text-muted">Loading issues...</p>
            </div>
          ) : (
            <>
              <style>{`
                .issues-table tbody td {
                  border-top: 1px solid #e9ecef;
                  padding: 12px 8px !important;
                }
                .issues-table thead th {
                  border-bottom: 2px solid #dee2e6;
                  padding: 12px 8px !important;
                }
                .issues-table tbody tr:hover {
                  background-color: #f8f9fa !important;
                }
                
                /* Category Filter Tabs */
                .category-filter-tabs {
                  display: flex;
                  border-bottom: 1px solid #dee2e6;
                  margin: 0;
                  padding: 0;
                }
                .category-filter-tabs .nav-item {
                  flex: 1;
                  list-style: none;
                }
                .category-filter-tabs .nav-link {
                  border: 1px solid #dee2e6;
                  border-bottom: none;
                  border-radius: 4px 4px 0 0;
                  background-color: #f8f9fa;
                  color: #495057;
                  transition: all 0.2s ease;
                  padding: 10px 16px;
                  display: block;
                  text-decoration: none;
                  min-width: 100px;
                }
                .category-filter-tabs .nav-link:hover {
                  background-color: #e9ecef;
                  color: #212529;
                  text-decoration: none;
                }
                .category-filter-tabs .nav-link.active {
                  background-color: #667eea;
                  color: white;
                  border-color: #667eea;
                  font-weight: 600;
                  position: relative;
                  z-index: 1;
                }
                .category-filter-tabs .nav-link.active:hover {
                  background-color: #5568d3;
                  border-color: #5568d3;
                  color: white;
                }
                
                @media (max-width: 768px) {
                  .category-filter-tabs {
                    max-width: 100% !important;
                  }
                  .category-filter-tabs .nav-link {
                    padding: 8px 12px;
                    font-size: 12px;
                    min-width: auto;
                  }
                }
                
                /* Mobile Card Layout */
                @media (max-width: 768px) {
                  .issues-table-desktop {
                    display: none;
                  }
                  .issues-card-mobile {
                    display: block;
                  }
                  .issues-table {
                    font-size: 12px;
                  }
                  
                  .modal-dialog {
                    margin: 10px !important;
                    max-width: calc(100% - 20px) !important;
                  }
                  
                  .modal-body {
                    padding: 12px !important;
                    font-size: 14px;
                  }
                  
                  .modal-header h5 {
                    font-size: 16px;
                  }
                  
                  .btn {
                    font-size: 12px;
                    padding: 6px 12px;
                  }
                }
                
                @media (min-width: 769px) {
                  .issues-table-desktop {
                    display: block;
                  }
                  .issues-card-mobile {
                    display: none;
                  }
                }
                
                .issue-card {
                  border: 1px solid #e9ecef;
                  border-radius: 8px;
                  margin-bottom: 16px;
                  padding: 16px;
                  background: white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                  transition: all 0.2s ease;
                }
                
                .issue-card:hover {
                  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
                  border-left: 4px solid #667eea;
                }
                
                .issue-card-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  margin-bottom: 12px;
                  padding-bottom: 12px;
                  border-bottom: 1px solid #e9ecef;
                }
                
                .issue-card-field {
                  margin-bottom: 12px;
                }
                
                .issue-card-label {
                  font-weight: 600;
                  font-size: 11px;
                  color: #6c757d;
                  text-transform: uppercase;
                  margin-bottom: 4px;
                  letter-spacing: 0.5px;
                }
                
                .issue-card-value {
                  font-size: 13px;
                  color: #212529;
                  word-break: break-word;
                }
                
                @media (max-width: 576px) {
                  .issue-card {
                    padding: 12px;
                    margin-bottom: 12px;
                  }
                  
                  .issue-card-header {
                    flex-direction: column;
                    gap: 8px;
                  }
                  
                  .block-header h1 {
                    font-size: 20px;
                  }
                  
                  .welcome-textSmall {
                    font-size: 14px;
                  }
                }
              `}</style>
              
              {/* Bulk Action Bar */}
              {selectedIssues.size > 0 && (
                <div className="alert alert-info d-flex justify-content-between align-items-center mb-3" style={{ padding: "12px 16px" }}>
                  <div>
                    <strong>{selectedIssues.size}</strong> issue(s) selected
                  </div>
                  <div className="d-flex gap-2">
                    {/* Show all options when not in false-positive or remediated tabs */}
                    {categoryFilter !== "false-positive" && categoryFilter !== "remidiated" && (
                      <>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => {
                            const issueIds = Array.from(selectedIssues);
                            handleMultiIssueAction(issueIds, 'markAsFalsePositive');
                          }}
                        >
                          <i className="fa fa-times-circle mr-1"></i> Mark as False Positive
                        </button>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => {
                            const issueIds = Array.from(selectedIssues);
                            handleMultiIssueAction(issueIds, 'markAsRemidiated');
                          }}
                        >
                          <i className="fa fa-check-circle mr-1"></i> Mark as Remediated
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => {
                            const issueIds = Array.from(selectedIssues);
                            handleMultiIssueAction(issueIds, 'markasLegitimate');
                          }}
                        >
                          <i className="fa fa-clock mr-1"></i> Multi Snooze
                        </button>
                      </>
                    )}
                    {/* Show only Unsnooze when in false-positive or remediated tabs */}
                    {(categoryFilter === "false-positive" || categoryFilter === "remidiated") && (
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => {
                          const issueIds = Array.from(selectedIssues);
                          //handleMultiIssueAction(issueIds, 'markasLegitimate');
                          handleSnoozeAction(issueIds, categoryFilter);
                        }}
                      >
                        <i className="fa fa-undo mr-1"></i> Unsnooze Selected
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setSelectedIssues(new Set())}
                    >
                      <i className="fa fa-times mr-1"></i> Clear Selection
                    </button>
                  </div>
                </div>
              )}

              {/* Desktop Table View */}
              <div className="issues-table-desktop">
                <div className="table-responsive">
                  <table className="table table-custom spacing5 mb-0 issues-table" style={{ fontSize: "13px", tableLayout: "fixed", width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th style={{ fontWeight: "600", width: "3%", minWidth: "40px", padding: "12px 8px", textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedIssues.size > 0 && selectedIssues.size === paginatedResults.length}
                            onChange={toggleSelectAll}
                            style={{ cursor: "pointer" }}
                          />
                        </th>
                        <th style={{ fontWeight: "600", width: "13%", minWidth: "130px", padding: "12px 8px" }}>VIP Member</th>
                        <th style={{ fontWeight: "600", width: "15%", minWidth: "150px", padding: "12px 8px" }}>Affected Email</th>
                        <th style={{ fontWeight: "600", width: "10%", minWidth: "100px", padding: "12px 8px" }}>Type</th>
                        <th style={{ fontWeight: "600", width: "8%", minWidth: "80px", padding: "12px 8px" }}>Severity</th>
                        <th style={{ fontWeight: "600", width: "20%", minWidth: "180px", padding: "12px 8px" }}>Description</th>
                        <th style={{ fontWeight: "600", width: "12%", minWidth: "120px", padding: "12px 8px" }}>Source</th>
                        <th style={{ fontWeight: "600", width: "13%", minWidth: "130px", padding: "12px 8px" }}>Detected At</th>
                        <th style={{ fontWeight: "600", width: "11%", minWidth: "110px", padding: "12px 8px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                    {paginatedResults.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center p-5">
                          <div className="py-4">
                            <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                            <p className="text-muted mb-0">
                              {filteredResults.length === 0 && allResults.length > 0
                                ? "No issues match your filters"
                                : "No issues found"}
                            </p>
                            {(searchTerm || severityFilter || moduleFilter) && (
                              <button
                                className="btn btn-link btn-sm mt-2"
                                onClick={() => {
                                  setSearchTerm("");
                                  setSeverityFilter("");
                                  setModuleFilter("");
                                }}
                              >
                                Clear filters
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedResults.map((result, index) => {
                        // Extract data from flattened result structure
                        // Get the actual issue ID - ensure it's always defined
                        const issueId = result?.issueId || result?._id || result?.id;
                        const scanId = result?.scanId || "N/A";
                        const vipMember = result?.vipMember ||  {};
                        const vipMemberName = typeof vipMember === "object" 
                          ? vipMember.name || (Array.isArray(vipMember.email) ? vipMember.email[0] : vipMember.email) || "N/A"
                          : vipMember || "N/A";
                        const moduleType = result.moduleType || result.module || "N/A";
                        // Format type for better display
                        const rawType = result.type || result.category || moduleType;
                        const type = rawType
                          ? rawType
                              .split("_")
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(" ")
                          : "N/A";
                        const severity = result.severity || result.risk || result.priority || "N/A";
                        // Use the processed description from flattened result (already a string)
                        const description = result.description || "N/A";
                        const source = result.source || result.platform || result.origin || result.domain || "N/A";
                        const detectedAt = result.detectedAt || result.timestamp || result.createdAt || result.date || result.foundAt;

                        // Determine severity badge class
                        const getSeverityBadgeClass = (sev) => {
                          const sevLower = sev.toLowerCase();
                          if (sevLower === "high" || sevLower === "critical") {
                            return "badge-danger";
                          } else if (sevLower === "medium") {
                            return "badge-warning";
                          } else if (sevLower === "low") {
                            return "badge-info";
                          }
                          return "badge-secondary";
                        };

                        return (
                          <tr 
                            key={issueId || index}
                            style={{ 
                              transition: "all 0.2s ease",
                              borderLeft: "3px solid transparent"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderLeftColor = "#667eea";
                              e.currentTarget.style.backgroundColor = "#f8f9fa";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderLeftColor = "transparent";
                              e.currentTarget.style.backgroundColor = "";
                            }}
                          >
                            <td style={{ padding: "12px 8px", verticalAlign: "middle", textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={selectedIssues.has(issueId)}
                                onChange={() => toggleIssueSelection(issueId)}
                                style={{ cursor: "pointer" }}
                              />
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle", wordBreak: "break-word" }}>
                              <div style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                {vipMemberName}
                              </div>
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle", wordBreak: "break-word" }}>
                              {result.email ? (
                                Array.isArray(result.email) ? (
                                  <div style={{ fontSize: "11px" }}>
                                    {result.email.map((email, idx) => {
                                      const safeEmail = validateEmail(email);
                                      return (
                                        <div key={idx} style={{ marginBottom: "4px" }}>
                                          {safeEmail ? (
                                            <a href={`mailto:${safeEmail}`} style={{ wordBreak: "break-word" }}>
                                              {email}
                                            </a>
                                          ) : (
                                            <span style={{ wordBreak: "break-word" }}>{email}</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: "11px" }}>
                                    {(() => {
                                      const safeEmail = validateEmail(result.email);
                                      return safeEmail ? (
                                        <a href={`mailto:${safeEmail}`} style={{ wordBreak: "break-word" }}>
                                          {result.email}
                                        </a>
                                      ) : (
                                        <span style={{ wordBreak: "break-word" }}>{result.email}</span>
                                      );
                                    })()}
                                  </div>
                                )
                              ) : (
                                <span className="text-muted" style={{ fontSize: "11px" }}>N/A</span>
                              )}
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>
                              <span className="badge badge-light" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                                {type}
                              </span>
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>
                              <div className="d-flex align-items-center gap-1">
                                <span className={`badge ${getSeverityBadgeClass(severity)}`} style={{ fontSize: "11px" }}>
                                  {severity}
                                </span>
                                {(result.status === 'false-positive' || result.category === 'false-positive') && (
                                  <span className="badge badge-warning" style={{ fontSize: "10px" }} title="False Positive">
                                    <i className="fa fa-times-circle"></i>
                                  </span>
                                )}
                                {(result.status === 'remidiated' || result.category === 'remidiated') && (
                                  <span className="badge badge-success" style={{ fontSize: "10px" }} title="Remediated">
                                    <i className="fa fa-check-circle"></i>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "top", wordBreak: "break-word" }}>
                              {expandedDescriptions.has(index) ? (
                                <div>
                                  <div style={{ 
                                    lineHeight: "1.5",
                                    fontSize: "12px",
                                    marginBottom: "8px",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word"
                                  }}>
                                    {description}
                                  </div>
                                  <button
                                    className="btn btn-link btn-sm p-0"
                                    style={{ fontSize: "11px", padding: "2px 0 !important" }}
                                    onClick={() => toggleDescription(index)}
                                  >
                                    Show Less
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ 
                                    maxHeight: "60px", 
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    lineHeight: "1.4",
                                    fontSize: "12px"
                                  }}>
                                    {description}
                                  </div>
                                  {description && description.trim().length > 0 && (
                                    <button
                                      className="btn btn-link btn-sm p-0 mt-1"
                                      style={{ fontSize: "11px", padding: "2px 0 !important" }}
                                      onClick={() => toggleDescription(index)}
                                    >
                                      Show More
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle", wordBreak: "break-word" }}>
                              <span style={{ fontSize: "11px" }}>{source}</span>
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle" }}>
                              {detectedAt ? (
                                <div style={{ fontSize: "11px", lineHeight: "1.4" }}>
                                  {new Date(detectedAt).toLocaleDateString()}
                                  <br />
                                  <small className="text-muted" style={{ fontSize: "10px" }}>
                                    {new Date(detectedAt).toLocaleTimeString()}
                                  </small>
                                </div>
                              ) : (
                                <span style={{ fontSize: "11px" }}>N/A</span>
                              )}
                            </td>
                            <td style={{ padding: "12px 8px", verticalAlign: "middle", textAlign: "center" }}>
                              <div className="btn-group-vertical" role="group" style={{ gap: "4px" }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  style={{ fontSize: "11px", padding: "4px 8px", whiteSpace: "nowrap" }}
                                  onClick={() => handleShowDetails(result)}
                                >
                                  <i className="fa fa-info-circle mr-1"></i> Details
                                </button>
                                <div className="btn-group" role="group" style={{ position: "relative" }}>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const dropdownId = issueId || index;
                                      setOpenDropdowns(prev => {
                                        // Close all other dropdowns and toggle current one
                                        const isCurrentlyOpen = prev[dropdownId];
                                        return isCurrentlyOpen ? {} : { [dropdownId]: true };
                                      });
                                    }}
                                    style={{ fontSize: "10px", padding: "2px 6px" }}
                                  >
                                    <i className="fa fa-ellipsis-v"></i>
                                  </button>
                                  {openDropdowns[issueId || index] && (
                                    <div 
                                      className="dropdown-menu dropdown-menu-right show"
                                      style={{ 
                                        position: "absolute", 
                                        right: 0, 
                                        top: "100%", 
                                        zIndex: 1000,
                                        minWidth: "200px"
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      {/* Only show "Mark as False Positive" if not in false-positive tab and issue is not already false positive */}
                                      {(categoryFilter !== "false-positive" && result.status !== 'false-positive' && result.category !== 'false-positive') && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          style={{ fontSize: "12px", cursor: "pointer" }}
                                          onMouseDown={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (issueId) {
                                              handleIssueAction(issueId, 'markAsFalsePositive');
                                            } else {
                                              console.error("Issue ID is missing:", { result, issueId });
                                              toastr.error('Error', 'Issue ID not found');
                                            }
                                          }}
                                        >
                                          <i className="fa fa-times-circle mr-2 text-warning"></i> Mark as False Positive
                                        </button>
                                      )}
                                      {/* Only show "Mark as Remediated" if not in remediated tab and issue is not already remediated */}
                                      {(categoryFilter !== "remidiated" && result.status !== 'remidiated' && result.category !== 'remidiated') && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          style={{ fontSize: "12px", cursor: "pointer" }}
                                          onMouseDown={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            console.log("Mark as Remediated clicked, issueId:", issueId);
                                            if (issueId) {
                                              handleIssueAction(issueId, 'markAsRemidiated');
                                            } else {
                                              console.error("Issue ID is missing:", { result, issueId });
                                              toastr.error('Error', 'Issue ID not found');
                                            }
                                          }}
                                        >
                                          <i className="fa fa-check-circle mr-2 text-success"></i> Mark as Remediated
                                        </button>
                                      )}
                                      {/* Show "Multi Snooze" only if not in false-positive or remediated tabs */}
                                      {categoryFilter !== "false-positive" && categoryFilter !== "remidiated" && (
                                        <>
                                          <div className="dropdown-divider"></div>
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            style={{ fontSize: "12px", cursor: "pointer" }}
                                            onMouseDown={(e) => {
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              console.log("Multi Snooze clicked, issueId:", issueId);
                                              if (issueId) {
                                                handleIssueAction(issueId, 'markasLegitimate');
                                              } else {
                                                console.error("Issue ID is missing:", { result, issueId });
                                                toastr.error('Error', 'Issue ID not found');
                                              }
                                            }}
                                          >
                                            <i className="fa fa-clock mr-2 text-info"></i> Multi Snooze
                                          </button>
                                        </>
                                      )}
                                      {/* Show "Unsnooze" if in false-positive or remediated tabs - ONLY option in those tabs */}
                                      {(categoryFilter === "false-positive" || categoryFilter === "remidiated") && (
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          style={{ fontSize: "12px", cursor: "pointer" }}
                                          onMouseDown={(e) => {
                                            e.stopPropagation();
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (issueId) {
                                              if (categoryFilter === "false-positive") {
                                                handleSnoozeAction(issueId, 'false-positive');
                                              } else if (categoryFilter === "remidiated") {
                                                handleSnoozeAction(issueId, 'remidiated');
                                              }
                                            } else {
                                              console.error("Issue ID is missing:", { result, issueId });
                                              toastr.error('Error', 'Issue ID not found');
                                            }
                                          }}
                                        >
                                          <i className="fa fa-undo mr-2 text-info"></i> Unsnooze
                                        </button>
                                      )}
                                      {/* Show "Unsnooze" in All tab if issue is already false positive or remediated */}
                                      {categoryFilter !== "false-positive" && categoryFilter !== "remidiated" && (result.status === 'false-positive' || result.category === 'false-positive' || result.status === 'remidiated' || result.category === 'remidiated') && (
                                        <>
                                          <div className="dropdown-divider"></div>
                                          <button
                                            type="button"
                                            className="dropdown-item"
                                            style={{ fontSize: "12px", cursor: "pointer" }}
                                            onMouseDown={(e) => {
                                              e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (issueId) {
                                                handleIssueAction(issueId, 'markasLegitimate');
                                              } else {
                                                console.error("Issue ID is missing:", { result, issueId });
                                                toastr.error('Error', 'Issue ID not found');
                                              }
                                            }}
                                          >
                                            <i className="fa fa-undo mr-2 text-info"></i> Unsnooze
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                </div>
              </div>
              
              {/* Mobile Card View */}
              <div className="issues-card-mobile">
                {paginatedResults.length === 0 ? (
                  <div className="text-center p-5">
                    <div className="py-4">
                      <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                      <p className="text-muted mb-0">
                        {filteredResults.length === 0 && allResults.length > 0
                          ? "No issues match your filters"
                          : "No issues found"}
                      </p>
                      {(searchTerm || severityFilter || moduleFilter) && (
                        <button
                          className="btn btn-link btn-sm mt-2"
                          onClick={() => {
                            setSearchTerm("");
                            setSeverityFilter("");
                            setModuleFilter("");
                          }}
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  paginatedResults.map((result, index) => {
                    // Extract data from flattened result structure
                    // Get the actual issue ID - ensure it's always defined
                    const issueId = result?.issueId || result?._id || result?.id;
                    const vipMember = result.vipMember || {};
                    const vipMemberName = typeof vipMember === "object" 
                      ? vipMember.name || (Array.isArray(vipMember.email) ? vipMember.email[0] : vipMember.email) || "N/A"
                      : vipMember || "N/A";
                    const moduleType = result.moduleType || result.module || "N/A";
                    const rawType = result.type || result.category || moduleType;
                    const type = rawType
                      ? rawType
                          .split("_")
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(" ")
                      : "N/A";
                    const severity = result.severity || result.risk || result.priority || "N/A";
                    // Use the processed description from flattened result (already a string)
                    const description = result.description || "N/A";
                    const source = result.source || result.platform || result.origin || result.domain || "N/A";
                    const detectedAt = result.detectedAt || result.timestamp || result.createdAt || result.date || result.foundAt;

                    const getSeverityBadgeClass = (sev) => {
                      const sevLower = sev.toLowerCase();
                      if (sevLower === "high" || sevLower === "critical") {
                        return "badge-danger";
                      } else if (sevLower === "medium") {
                        return "badge-warning";
                      } else if (sevLower === "low") {
                        return "badge-info";
                      }
                      return "badge-secondary";
                    };

                    return (
                      <div key={issueId || index} className="issue-card">
                        <div className="issue-card-header">
                          <div>
                            <div className="issue-card-value" style={{ fontWeight: "600", fontSize: "14px" }}>
                              {vipMemberName}
                            </div>
                            <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "4px" }}>
                              {type}
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span className={`badge ${getSeverityBadgeClass(severity)}`} style={{ fontSize: "11px" }}>
                              {severity}
                            </span>
                            {(result.status === 'false-positive' || result.category === 'false-positive') && (
                              <span className="badge badge-warning" style={{ fontSize: "10px" }} title="False Positive">
                                <i className="fa fa-times-circle"></i> False Positive
                              </span>
                            )}
                            {(result.status === 'remidiated' || result.category === 'remidiated') && (
                              <span className="badge badge-success" style={{ fontSize: "10px" }} title="Remediated">
                                <i className="fa fa-check-circle"></i> Remediated
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="issue-card-field">
                          <div className="issue-card-label">Affected Email</div>
                          <div className="issue-card-value">
                            {result.email ? (
                              Array.isArray(result.email) ? (
                                <div>
                                  {result.email.map((email, idx) => {
                                    const safeEmail = validateEmail(email);
                                    return (
                                      <div key={idx} style={{ marginBottom: "4px" }}>
                                        {safeEmail ? (
                                          <a href={`mailto:${safeEmail}`} style={{ wordBreak: "break-word" }}>
                                            {email}
                                          </a>
                                        ) : (
                                          <span style={{ wordBreak: "break-word" }}>{email}</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div>
                                  {(() => {
                                    const safeEmail = validateEmail(result.email);
                                    return safeEmail ? (
                                      <a href={`mailto:${safeEmail}`} style={{ wordBreak: "break-word" }}>
                                        {result.email}
                                      </a>
                                    ) : (
                                      <span style={{ wordBreak: "break-word" }}>{result.email}</span>
                                    );
                                  })()}
                                </div>
                              )
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </div>
                        </div>

                        <div className="issue-card-field">
                          <div className="issue-card-label">Description</div>
                          <div className="issue-card-value">
                            {expandedDescriptions.has(index) ? (
                              <div>
                                <div style={{ 
                                  lineHeight: "1.5",
                                  fontSize: "13px",
                                  marginBottom: "8px",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word"
                                }}>
                                  {description}
                                </div>
                                <button
                                  className="btn btn-link btn-sm p-0"
                                  style={{ fontSize: "11px", padding: "2px 0 !important" }}
                                  onClick={() => toggleDescription(index)}
                                >
                                  Show Less
                                </button>
                              </div>
                            ) : (
                              <div>
                                <div style={{ 
                                  maxHeight: "60px", 
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.4",
                                  fontSize: "13px"
                                }}>
                                  {description}
                                </div>
                                {description && description.trim().length > 0 && (
                                  <button
                                    className="btn btn-link btn-sm p-0 mt-1"
                                    style={{ fontSize: "11px", padding: "2px 0 !important" }}
                                    onClick={() => toggleDescription(index)}
                                  >
                                    Show More
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-6">
                            <div className="issue-card-field">
                              <div className="issue-card-label">Source</div>
                              <div className="issue-card-value" style={{ fontSize: "12px" }}>
                                {source}
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="issue-card-field">
                              <div className="issue-card-label">Detected At</div>
                              <div className="issue-card-value" style={{ fontSize: "12px" }}>
                                {detectedAt ? (
                                  <div>
                                    {new Date(detectedAt).toLocaleDateString()}
                                    <br />
                                    <small className="text-muted" style={{ fontSize: "10px" }}>
                                      {new Date(detectedAt).toLocaleTimeString()}
                                    </small>
                                  </div>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="issue-card-field" style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e9ecef" }}>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-primary flex-fill"
                              style={{ fontSize: "12px" }}
                              onClick={() => handleShowDetails(result)}
                            >
                              <i className="fa fa-info-circle mr-1"></i> View Details
                            </button>
                            <div className="btn-group" style={{ position: "relative" }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const dropdownId = issueId || index;
                                  setOpenDropdowns(prev => {
                                    // Close all other dropdowns and toggle current one
                                    const isCurrentlyOpen = prev[dropdownId];
                                    return isCurrentlyOpen ? {} : { [dropdownId]: true };
                                  });
                                }}
                                style={{ fontSize: "12px" }}
                              >
                                <i className="fa fa-ellipsis-v"></i>
                              </button>
                              {openDropdowns[issueId || index] && (
                                <div 
                                  className="dropdown-menu dropdown-menu-right show"
                                  style={{ 
                                    position: "absolute", 
                                    right: 0, 
                                    top: "100%", 
                                    zIndex: 1000,
                                    minWidth: "200px"
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  {/* Only show "Mark as False Positive" if not in false-positive tab and issue is not already false positive */}
                                  {(categoryFilter !== "false-positive" && result.status !== 'false-positive' && result.category !== 'false-positive') && (
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      style={{ fontSize: "12px", cursor: "pointer" }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log("Mark as False Positive clicked, issueId:", issueId);
                                        if (issueId) {
                                          handleIssueAction(issueId, 'markAsFalsePositive');
                                        } else {
                                          console.error("Issue ID is missing:", { result, issueId });
                                          toastr.error('Error', 'Issue ID not found');
                                        }
                                      }}
                                    >
                                      <i className="fa fa-times-circle mr-2 text-warning"></i> Mark as False Positive
                                    </button>
                                  )}
                                  {/* Only show "Mark as Remediated" if not in remediated tab and issue is not already remediated */}
                                  {(categoryFilter !== "remidiated" && result.status !== 'remidiated' && result.category !== 'remidiated') && (
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      style={{ fontSize: "12px", cursor: "pointer" }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log("Mark as Remediated clicked, issueId:", issueId);
                                        if (issueId) {
                                          handleIssueAction(issueId, 'markAsRemidiated');
                                        } else {
                                          console.error("Issue ID is missing:", { result, issueId });
                                          toastr.error('Error', 'Issue ID not found');
                                        }
                                      }}
                                    >
                                      <i className="fa fa-check-circle mr-2 text-success"></i> Mark as Remediated
                                    </button>
                                  )}
                                  {/* Show "Multi Snooze" only if not in false-positive or remediated tabs */}
                                  {categoryFilter !== "false-positive" && categoryFilter !== "remidiated" && (
                                    <>
                                      <div className="dropdown-divider"></div>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        style={{ fontSize: "12px", cursor: "pointer" }}
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          console.log("Multi Snooze clicked, issueId:", issueId);
                                          if (issueId) {
                                            handleIssueAction(issueId, 'markasLegitimate');
                                          } else {
                                            console.error("Issue ID is missing:", { result, issueId });
                                            toastr.error('Error', 'Issue ID not found');
                                          }
                                        }}
                                      >
                                        <i className="fa fa-clock mr-2 text-info"></i> Multi Snooze
                                      </button>
                                    </>
                                  )}
                                  {/* Show "Unsnooze" if in false-positive or remediated tabs - ONLY option in those tabs */}
                                  {(categoryFilter === "false-positive" || categoryFilter === "remidiated") && (
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      style={{ fontSize: "12px", cursor: "pointer" }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log("Unsnooze clicked, issueId:", issueId);
                                        if (issueId) {
                                          handleIssueAction(issueId, 'markasLegitimate');
                                        } else {
                                          console.error("Issue ID is missing:", { result, issueId });
                                          toastr.error('Error', 'Issue ID not found');
                                        }
                                      }}
                                    >
                                      <i className="fa fa-undo mr-2 text-info"></i> Unsnooze
                                    </button>
                                  )}
                                  {/* Show "Unsnooze" in All tab if issue is already false positive or remediated */}
                                  {categoryFilter !== "false-positive" && categoryFilter !== "remidiated" && (result.status === 'false-positive' || result.category === 'false-positive' || result.status === 'remidiated' || result.category === 'remidiated') && (
                                    <>
                                      <div className="dropdown-divider"></div>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        style={{ fontSize: "12px", cursor: "pointer" }}
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          console.log("Unsnooze clicked, issueId:", issueId);
                                          if (issueId) {
                                            handleIssueAction(issueId, 'markasLegitimate');
                                          } else {
                                            console.error("Issue ID is missing:", { result, issueId });
                                            toastr.error('Error', 'Issue ID not found');
                                          }
                                        }}
                                      >
                                        <i className="fa fa-undo mr-2 text-info"></i> Unsnooze
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              {totalCount > 0 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 pt-3" style={{ borderTop: "1px solid #e9ecef", gap: "12px" }}>
                  <div className="dataTables_paginate">
                    {totalCount > pageSize && (
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={totalCount}
                        pageSize={pageSize}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    )}
                  </div>
                  {totalCount > 10 && (
                    <div className="d-flex align-items-center gap-2">
                      <label className="mb-0 text-muted" style={{ fontSize: "12px" }}>
                        Show:
                      </label>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(parseInt(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-control form-control-sm"
                        style={{ width: "auto", minWidth: "80px", fontSize: "12px" }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-muted" style={{ fontSize: "12px" }}>
                        per page
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedIssue && (
        <div 
          className="modal fade show" 
          style={{ 
            display: "block", 
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1050,
            overflow: "auto"
          }}
          onClick={handleCloseDetails}
        >
          <div 
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            style={{ margin: "1.75rem auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: "#667eea", color: "white" }}>
                <h5 className="modal-title">
                  <i className="fa fa-info-circle mr-2"></i>
                  Issue Details
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={handleCloseDetails}
                  style={{ color: "white", opacity: 0.8 }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto", padding: "16px" }}>
                <div className="row mb-3">
                  <div className="col-12 col-md-6 mb-2">
                    <strong>Type:</strong>
                    <div className="mt-1">
                      <span className="badge badge-light">
                        {selectedIssue.type
                          ? selectedIssue.type
                              .split("_")
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(" ")
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <strong>Severity:</strong>
                    <div className="mt-1">
                      <span className={`badge ${
                        (selectedIssue.severity || "").toLowerCase() === "high" || (selectedIssue.severity || "").toLowerCase() === "critical"
                          ? "badge-danger"
                          : (selectedIssue.severity || "").toLowerCase() === "medium"
                          ? "badge-warning"
                          : "badge-info"
                      }`}>
                        {selectedIssue.severity || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12 col-md-6 mb-2">
                    <strong>VIP Member:</strong>
                    <div className="mt-1">
                      {(() => {
                        const vipMember = selectedIssue.vipMember || {};
                        const name = typeof vipMember === "object" 
                          ? vipMember.name || "N/A"
                          : vipMember || "N/A";
                        const position = typeof vipMember === "object" ? vipMember.position : null;
                        return (
                          <div>
                            <div>{name}</div>
                            {position && <small className="text-muted">{position}</small>}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="col-12 col-md-6 mb-2">
                    <strong>Scan ID:</strong>
                    <div className="mt-1" style={{ fontFamily: "monospace", fontSize: "12px" }}>
                      {selectedIssue.scanId || "N/A"}
                    </div>
                  </div>
                </div>

                {/* Email Information */}
                {selectedIssue.email && (
                  <div className="mb-3">
                    <strong>Affected Email(s):</strong>
                    <div className="mt-1">
                      {Array.isArray(selectedIssue.email) ? (
                        <ul className="mb-0 pl-3">
                          {selectedIssue.email.map((email, idx) => {
                            const safeEmail = validateEmail(email);
                            return (
                              <li key={idx} style={{ wordBreak: "break-word" }}>
                                {safeEmail ? (
                                  <a href={`mailto:${safeEmail}`}>{email}</a>
                                ) : (
                                  <span>{email}</span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div style={{ wordBreak: "break-word" }}>
                          {(() => {
                            const safeEmail = validateEmail(selectedIssue.email);
                            return safeEmail ? (
                              <a href={`mailto:${safeEmail}`}>{selectedIssue.email}</a>
                            ) : (
                              <span>{selectedIssue.email}</span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* URL/Link Information */}
                {(selectedIssue.url || selectedIssue.link || selectedIssue.profileUrl) && (
                  <div className="mb-3">
                    <strong>Link/URL:</strong>
                    <div className="mt-1">
                      {(() => {
                        const url = selectedIssue.url || selectedIssue.link || selectedIssue.profileUrl;
                        const validUrl = validateUrl(url);
                        if (validUrl) {
                          return (
                            <a href={validUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-word" }}>
                              {validUrl}
                            </a>
                          );
                        }
                        return <span style={{ wordBreak: "break-word" }}>{url}</span>;
                      })()}
                    </div>
                  </div>
                )}

                {/* Source/Platform */}
                {selectedIssue.source && (
                  <div className="mb-3">
                    <strong>Source/Platform:</strong>
                    <div className="mt-1">{selectedIssue.source}</div>
                  </div>
                )}

                {/* Registration (for email_registration type) */}
                {selectedIssue.registration && (
                  <div className="mb-3">
                    <strong>Registration Platform:</strong>
                    <div className="mt-1">{selectedIssue.registration}</div>
                  </div>
                )}

                {/* Database Name (for PII/Leaked Credentials) */}
                {selectedIssue.dbname && (
                  <div className="mb-3">
                    <strong>Database/Source:</strong>
                    <div className="mt-1">{selectedIssue.dbname}</div>
                  </div>
                )}

                {/* Additional PII Information */}
                {selectedIssue.type === "pii" && (
                  <>
                    {selectedIssue.name && (
                      <div className="mb-3">
                        <strong>Name Found:</strong>
                        <div className="mt-1">{selectedIssue.name}</div>
                      </div>
                    )}
                    {selectedIssue.address && (
                      <div className="mb-3">
                        <strong>Address:</strong>
                        <div className="mt-1">{selectedIssue.address}</div>
                      </div>
                    )}
                    {selectedIssue.phone && (
                      <div className="mb-3">
                        <strong>Phone:</strong>
                        <div className="mt-1">{selectedIssue.phone}</div>
                      </div>
                    )}
                    {selectedIssue.username && (
                      <div className="mb-3">
                        <strong>Username:</strong>
                        <div className="mt-1">{selectedIssue.username}</div>
                      </div>
                    )}
                  </>
                )}

                {/* Leaked Credential Information */}
                {selectedIssue.type === "leaked_credential" && selectedIssue.password && (
                  <div className="mb-3">
                    <strong>Leaked Password:</strong>
                    <div className="mt-1" style={{ fontFamily: "monospace", backgroundColor: "#f8f9fa", padding: "8px", borderRadius: "4px" }}>
                      {selectedIssue.password}
                    </div>
                  </div>
                )}

                {/* Social Profile Data */}
                {selectedIssue.type === "social_profile" && selectedIssue.profileData && (
                  <div className="mb-3">
                    <strong>Profile Information:</strong>
                    <div className="mt-2">
                      {selectedIssue.profileData.username && (
                        <div className="mb-2">
                          <strong>Username:</strong> {selectedIssue.profileData.username}
                        </div>
                      )}
                      {selectedIssue.profileData.name && (
                        <div className="mb-2">
                          <strong>Profile Name:</strong> {selectedIssue.profileData.name}
                        </div>
                      )}
                      {selectedIssue.profileData.platform && (
                        <div className="mb-2">
                          <strong>Platform:</strong> {selectedIssue.profileData.platform}
                        </div>
                      )}
                      {selectedIssue.profileData.summary && (
                        <div className="mb-2">
                          <strong>Summary:</strong>
                          <div className="mt-1" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {selectedIssue.profileData.summary}
                          </div>
                        </div>
                      )}
                      {selectedIssue.profileData.risk_analysis && (
                        <div className="mb-2">
                          <strong>Risk Analysis:</strong>
                          <div className="mt-1" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {selectedIssue.profileData.risk_analysis}
                          </div>
                        </div>
                      )}
                      {selectedIssue.profileData.risk_score && (
                        <div className="mb-2">
                          <strong>Risk Score:</strong> {selectedIssue.profileData.risk_score}
                        </div>
                      )}
                      {selectedIssue.profileData.threat_indicators && Array.isArray(selectedIssue.profileData.threat_indicators) && (
                        <div className="mb-2">
                          <strong>Threat Indicators:</strong>
                          <ul className="mt-1 mb-0 pl-3">
                            {selectedIssue.profileData.threat_indicators.map((indicator, idx) => (
                              <li key={idx} style={{ wordBreak: "break-word" }}>{indicator}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Screenshot for Social Media */}
                {(selectedIssue.screenshot || selectedIssue.screenshot_url || selectedIssue.profileData?.screenshot || selectedIssue.profileData?.screenshot_url) && (
                  <div className="mb-3">
                    <strong>Screenshot:</strong>
                    <div className="mt-2">
                      {(() => {
                        const screenshotUrl = selectedIssue.screenshot || selectedIssue.screenshot_url || selectedIssue.profileData?.screenshot || selectedIssue.profileData?.screenshot_url;
                        const fullImagePath = screenshotUrl.startsWith('http') ? screenshotUrl : `${API_URL}${screenshotUrl.startsWith('/') ? '' : '/'}${screenshotUrl}`;
                        
                        return (
                          <div className="text-center">
                            <img
                              src={fullImagePath}
                              alt="Social Media Screenshot"
                              className="img-fluid rounded"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "400px",
                                objectFit: "contain",
                                border: "1px solid #e9ecef",
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                              }}
                              onClick={() => window.open(fullImagePath, '_blank')}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const errorDiv = e.target.nextSibling;
                                if (errorDiv) {
                                  errorDiv.style.display = 'block';
                                }
                              }}
                            />
                            <div 
                              style={{ 
                                display: 'none', 
                                padding: '20px', 
                                textAlign: 'center', 
                                fontSize: '14px', 
                                color: '#666',
                                border: '1px solid #e9ecef',
                                borderRadius: '4px'
                              }}
                            >
                              <i className="fa fa-image mr-2"></i>
                              Screenshot not available
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Click image to view full size
                              </small>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedIssue.description && (
                  <div className="mb-3">
                    <strong>Description:</strong>
                    <div className="mt-1" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {selectedIssue.description}
                    </div>
                  </div>
                )}

                {/* Detected At */}
                {selectedIssue.detectedAt && (
                  <div className="mb-3">
                    <strong>Detected At:</strong>
                    <div className="mt-1">
                      {new Date(selectedIssue.detectedAt).toLocaleString()}
                    </div>
                  </div>
                )}

                {/* Module Type */}
                {selectedIssue.moduleType && (
                  <div className="mb-3">
                    <strong>Module:</strong>
                    <div className="mt-1">{selectedIssue.moduleType}</div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {(() => {
                  const validUrl = validateUrl(selectedIssue.url || selectedIssue.link || selectedIssue.profileUrl);
                  return (
                    <>
                      {validUrl && (
                        <a
                          href={validUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-info"
                        >
                          <i className="fa fa-external-link mr-1"></i> Open Link
                        </a>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCloseDetails}
                      >
                        Close
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommendation Modal */}
      {isRecommendationModal && (
        <div className="recommendation-modal-overlay" onClick={() => setIsRecommendationModal(false)}>
          <div className="recommendation-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="recommendation-modal-close" onClick={() => setIsRecommendationModal(false)}>
              &times;
            </button>
            
            <div className="recommendation-modal-content">
              <div className="recommendation-tabs">
                <button
                  className={`recommendation-tab ${activeRecommendationTab === 'guide' ? 'active' : ''}`}
                  onClick={() => setActiveRecommendationTab('guide')}
                >
                  Guidance
                </button>
                <button
                  className={`recommendation-tab ${activeRecommendationTab === 'checklist' ? 'active' : ''}`}
                  onClick={() => setActiveRecommendationTab('checklist')}
                >
                  Action Checklist
                </button>
              </div>

              {/* GUIDANCE TAB */}
              <div className={`recommendation-tab-content ${activeRecommendationTab === 'guide' ? 'active' : ''}`}>
                <div className="guide-intro">
                  BrandSek continuously monitors digital risk exposure related to executives, board members,
                  and other high-value individuals. Executive identities are prime targets for impersonation,
                  fraud, phishing, extortion, and reputational attacks.
                </div>

                <details className="checklist-details" open>
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>What Executive Monitoring Covers</h3>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Dark web forums, marketplaces, and private leak channels</li>
                      <li>Telegram groups, Discord servers, and encrypted communities</li>
                      <li>Surface web sources including social media platforms and blogs</li>
                      <li>Public data brokers and people search platforms</li>
                    </ul>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Types of Executive Risks Detected</h3>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Exposure of personal information (email, phone, address, family details)</li>
                      <li>Credential leaks linked to executive identities</li>
                      <li>Fake or impersonating social media profiles</li>
                      <li>Malicious posts, misinformation, or reputation attacks</li>
                      <li>Mentions in threat actor discussions or targeting lists</li>
                    </ul>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Why Executives Are High-Value Targets</h3>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Access to sensitive business and financial information</li>
                      <li>Authority to approve payments and strategic decisions</li>
                      <li>Public visibility increases social engineering success</li>
                      <li>Personal compromise can lead to organizational breaches</li>
                    </ul>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Business, Legal & Reputational Impact</h3>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <p>
                      Executive compromise may result in financial fraud, insider impersonation,
                      data breaches, regulatory scrutiny, and reputational damage. Proactive
                      monitoring reduces response time and limits downstream impact.
                    </p>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Governance & Compliance Alignment</h3>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <p>
                      Executive digital risk monitoring supports ISO 27001, RBI Cyber Security
                      Framework, CERT-In advisories, and board-level risk governance expectations.
                    </p>
                  </div>
                </details>
              </div>

              {/* CHECKLIST TAB */}
              <div className={`recommendation-tab-content ${activeRecommendationTab === 'checklist' ? 'active' : ''}`}>
                <details className="checklist-details" open>
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Identity & Credential Protection</h3>
                      <span className="severity-badge high">High</span>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Reset compromised executive credentials</li>
                      <li>Enforce MFA on executive accounts</li>
                      <li>Review access privileges and delegations</li>
                    </ul>
                    <div className="compliance-tag">ISO 27001 A.5.17 · RBI IAM</div>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Impersonation & Social Media Abuse</h3>
                      <span className="severity-badge high">High</span>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Identify fake or impersonating profiles</li>
                      <li>Initiate takedown with platforms</li>
                      <li>Monitor reuse of executive photos or names</li>
                    </ul>
                    <div className="compliance-tag">ISO 27001 A.8.16</div>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Personal Information Exposure</h3>
                      <span className="severity-badge medium">Medium</span>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Identify exposed personal data</li>
                      <li>Request removal from data broker sites</li>
                      <li>Assess physical and social engineering risk</li>
                    </ul>
                    <div className="compliance-tag">ISO 27001 A.5.34 · Privacy</div>
                  </div>
                </details>

                <details className="checklist-details">
                  <summary className="checklist-summary">
                    <div className="summary-left">
                      <h3>Ongoing Monitoring & Reporting</h3>
                      <span className="severity-badge low">Low</span>
                    </div>
                  </summary>
                  <div className="checklist-card-content">
                    <ul className="checklist-items">
                      <li>Maintain continuous monitoring coverage</li>
                      <li>Correlate executive risk with org-wide threats</li>
                      <li>Document findings for leadership reporting</li>
                    </ul>
                    <div className="compliance-tag">ISO 27001 A.5.25 · Board Oversight</div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Issues;

